import type { ClientsSummary, ConfigSignals, InsurerFormEntry, Page } from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useApiKeys } from '../integrations/api';
import { useInsurerCatalogue } from '../insurers/api';
import type { Member, People } from '../people/api';
import { useItemBodies, useItems, usePendingRelease, type Release } from '../studio/api';
import { useSeesPortfolio } from '../portfolio/api';
import type { SettingsAccess } from './access';

/**
 * What Tenant 360 shows (task 102), each from the route its reader can already call and only when they may:
 * the configuration checks' signals and the overview's figures.
 */
export function useTenantSignals(a: SettingsAccess) {
  const { tenant } = useSession();
  // When the page opened: "expires soon" is judged against it.
  const [now] = useState(() => Date.now());
  const portfolio = useSeesPortfolio();
  const questions = useItemBodies('question', a.studio);
  const controls = useItems('control', a.studio);
  const journeys = useItems('journey', a.studio);
  const pending = usePendingRelease(a.studio);
  const forms = useInsurerCatalogue(a.insurerForms);
  const keys = useApiKeys(a.apiKeys);
  // In the lists' cache family, so a change on any list (an invitation sent, a client added) asks again.
  const people = useQuery({
    queryKey: ['list', tenant?.id, '/tenants/current/people', 'tenant-360'],
    queryFn: () => api<Page<Member> & People>('GET', '/tenants/current/people'),
    enabled: !!tenant && a.people,
  });
  const clients = useQuery({
    queryKey: ['list', tenant?.id, '/clients', 'tenant-360'],
    queryFn: () => api<Page<unknown> & { summary: ClientsSummary }>('GET', '/clients'),
    enabled: !!tenant && portfolio,
  });
  const releases = useQuery({
    queryKey: ['config', tenant?.id, 'releases', 'recent'],
    queryFn: () => api<Page<Release>>('GET', '/config/releases?page_size=12'),
    enabled: !!tenant && a.studio,
  });

  const signals: ConfigSignals = {
    now,
    questions: questions.data as ConfigSignals['questions'],
    controls: controls.data,
    pending_release: pending.data,
    insurer_forms: forms.data,
    invitations: people.data?.invitations,
    api_keys: keys.data,
  };
  const live = (xs: { key: string; retired_at: string | null }[] | undefined) =>
    xs && new Set(xs.filter((x) => !x.retired_at).map((x) => x.key)).size;
  const ready = (f: InsurerFormEntry) => !!f.pdf && f.template?.sha256 === f.pdf.sha256;
  return {
    signals,
    figures: {
      clients: clients.data?.summary.clients,
      journeys: live(journeys.data),
      questions: live(questions.data),
      controls: live(controls.data),
      forms: forms.data && { ready: forms.data.filter(ready).length, total: forms.data.length },
      people: people.data?.total,
      invitations: people.data?.invitations.length,
      keys: keys.data?.filter((k) => !k.revoked_at).length,
    },
    releases: releases.data?.items ?? [],
  };
}
